import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface FetchQmsElement {
  data: {
    levelCount?: any;
    qmsElement?: any;
    key?: string;
    id?: string;
    elementId: string;
    levelDefinitions?: Array<{
      id: string;
      name: string;
      level: number;
      description: string | null;
    }>;
    name?: string;
    description?: string;
    levels: Array<{ level: number; name: string }>;
    isActive?: boolean;
  };
}

export interface QmsElement {
  levelCount?: any;
  qmsElement?: any;
  key?: string;
  id?: string;
  elementId: string;
  levelDefinitions?: Array<{
    id: string;
    name: string;
    level: number;
    description: string | null;
  }>;
  name?: string;
  description?: string;
  levels: Array<{ level: number; name: string }>;
  isActive?: boolean;
  element?: {
    id: string;
  };
}

export interface GetQmsElement {
  id: string;
  qmsElement: {
    id: string;
    name: string;
    description: string;
  };
  isActive: boolean;
  levelCount: number;
  createdAt: string;
  updatedAt: string;
}

interface QmsMetadata {
  totalCount: number;
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface QmsElementsResponse {
  items: QmsElement[];
  meta: QmsMetadata;
}

interface QmsElementDetails {
  id: string;
  qmsElement: {
    id: string;
    name: string;
    description: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  levelDefinitions: Array<{
    id: string;
    name: string;
    level: number;
    description: string | null;
  }>;
}

interface QmsState {
  elements: QmsElementsResponse;
  selectedElement?: QmsElement;
  loading: boolean;
  error: string | null;
}

interface FetchedQmsElement {
  data: {
    success: boolean;
    message: string;
    element: {
      id: string;
      qmsElementId: string;
      companyId: string;
      isActive: boolean;
      createdById: string;
      updatedById: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: null | string;
    };
    levelDefinitions: Array<{
      id: string;
      companyElementId: string;
      name: string;
      level: number;
      description: null | string;
      createdAt: string;
      updatedAt: string;
      deletedAt: null | string;
    }>;
    operation: string;
  };
}

const initialState: QmsState = {
  elements: {
    items: [],
    meta: {
      totalCount: 0,
      count: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    },
  },
  loading: false,
  error: null,
};

const qmsSlice = createSlice({
  name: "qms",
  initialState,
  reducers: {
    fetchQmsElements: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchQmsElementsSuccess: (
      state,
      action: PayloadAction<QmsElementsResponse>,
    ) => {
      state.elements = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchQmsElementsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchQmsElementById: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    fetchQmsElementByIdSuccess: (state, action: PayloadAction<QmsElement>) => {
      state.selectedElement = {
        ...state.selectedElement,
        ...action.payload,
      };
      state.loading = false;
      state.error = null;
    },
    fetchQmsElementByIdFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    createQmsElement: (
      state,
      action: PayloadAction<Omit<QmsElement, "id">>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    createQmsElementSuccess: (state, action: PayloadAction<QmsElement>) => {
      state.elements.items.push(action.payload);
      state.elements.meta.count += 1;
      state.elements.meta.totalCount += 1;
      state.loading = false;
      state.error = null;
    },
    createQmsElementFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateQmsElement: (
      state,
      action: PayloadAction<{
        elementId: string;
        levels: Array<{ level: number; name: string }>;
      }>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateQmsElementSuccess: (
      state,
      action: PayloadAction<FetchedQmsElement>,
    ) => {
      const { element, levelDefinitions } = action.payload.data;

      const transformedElement: QmsElement = {
        id: element.id,
        elementId: element.qmsElementId,
        isActive: element.isActive,
        levelDefinitions: levelDefinitions.map((def) => ({
          id: def.id,
          name: def.name,
          level: def.level,
          description: def.description,
        })),
        levels: levelDefinitions.map((def) => ({
          level: def.level,
          name: def.name,
        })),
      };

      // Initialize elements.items if it doesn't exist
      if (!state.elements) {
        state.elements = {
          items: [],
          meta: {
            totalCount: 0,
            count: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
          },
        };
      }
      if (!state.elements.items) {
        state.elements.items = [];
      }

      const index = state.elements.items.findIndex(
        (el) => el.id === element.id,
      );

      if (index !== -1) {
        state.elements.items[index] = transformedElement;
      } else {
        // If the element is not found, add it to the items array
        state.elements.items.push(transformedElement);
      }

      state.loading = false;
      state.error = null;
    },
    updateQmsElementFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    deleteQmsElement: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    deleteQmsElementSuccess: (state, action: PayloadAction<string>) => {
      if (state.elements?.items) {
        state.elements = {
          ...state.elements,
          items: state.elements.items.filter((el) => el.id !== action.payload),
          meta: {
            ...state.elements.meta,
            count: Math.max(0, (state.elements.meta.count || 0) - 1),
            totalCount: Math.max(0, (state.elements.meta.totalCount || 0) - 1),
          },
        };
      } else {
        state.elements = {
          items: [],
          meta: {
            totalCount: 0,
            count: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
          },
        };
      }
      state.loading = false;
      state.error = null;
    },
    deleteQmsElementFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  fetchQmsElements,
  fetchQmsElementsSuccess,
  fetchQmsElementsFailure,
  fetchQmsElementById,
  fetchQmsElementByIdSuccess,
  fetchQmsElementByIdFailure,
  createQmsElement,
  createQmsElementSuccess,
  createQmsElementFailure,
  updateQmsElement,
  updateQmsElementSuccess,
  updateQmsElementFailure,
  deleteQmsElement,
  deleteQmsElementSuccess,
  deleteQmsElementFailure,
} = qmsSlice.actions;

export default qmsSlice.reducer;
